import type {
  Config,
  DialogFilter,
  DialogFilterSuggested,
  HelpAppConfig,
  InputPeer,
  InputNotifyPeer,
  InputPeerNotifySettings,
  InputUser,
  InputFile,
  InputMedia,
  InputSingleMedia,
  InputFileLocation,
  MessagesAffectedHistory,
  MessagesAffectedMessages,
  MessagesDialogs,
  MessagesDialogFilters,
  MessagesFilter,
  MessagesMessages,
  MessagesMessageEditData,
  MessagesMessageReactionsList,
  MessagesAvailableReactions,
  MessagesReactions,
  MessagesSavedReactionTags,
  MessagesPeerSettings,
  MessagesPeerDialogs,
  MessagesSearchCounter,
  MessageEntity,
  InputMessage,
  InputReplyTo,
  InputDialogPeer,
  PeerNotifySettings,
  PhotosPhotos,
  Reaction,
  UpdatesState,
  Updates,
  UploadFile,
  User,
  UsersUserFull
} from '@layer';

import type {ChatNotesCompatibilityAdapter} from './adapter';
import type {
  ChatNotesAttachment,
  ChatNotesAttachmentKind,
  ChatNotesFolder,
  ChatNotesNote,
  ChatNotesUser
} from './contract';
import {
  buildChatNotesWebKDialogs,
  buildChatNotesWebKHistory,
  buildChatNotesWebKMessages,
  CHAT_NOTES_UNFILED_FOLDER_ID,
  chatNotesFormattingToWebKEntity,
  chatNotesFolderToWebKUser,
  chatNotesFolderToWebKDialogFilter,
  chatNotesNoteToWebKMessage,
  chatNotesUserToWebKUser,
  chatNotesUnfiledToWebKUser,
  folderIdToWebKPeerId,
  webKEntityToChatNotesFormatting
} from './webk-model-adapter';
import {isChatNotesWebKSupportedMethod} from './webk-supported-methods';

const NOTES_PAGE_SIZE = 100;
const DEFAULT_REACTIONS = ['❤️', '👍', '🔥', '🎉', '😁', '🤔', '👎'];

type GetDialogsParams = {
  limit: number;
  offset_peer?: InputPeer;
};

type GetHistoryParams = {
  peer: InputPeer;
  offset_id: number;
  add_offset: number;
  limit: number;
};

type SendMessageParams = {
  peer: InputPeer;
  message: string;
  entities?: MessageEntity[];
  reply_to?: InputReplyTo;
  schedule_date?: number;
};

type EditMessageParams = SendMessageParams & {id: number};
type DeleteMessagesParams = {id: number[]};
type DeleteHistoryParams = {peer: InputPeer};
type GetPeerDialogsParams = {peers: InputDialogPeer[]};
type GetMessagesParams = {id: InputMessage[]};
type ForwardMessagesParams = {
  from_peer: InputPeer;
  id: number[];
  to_peer: InputPeer;
  schedule_date?: number;
};
type UpdatePinnedMessageParams = {peer: InputPeer; id: number; unpin?: boolean};
type SendReactionParams = {peer: InputPeer; msg_id: number; reaction?: Reaction[]};
type SearchParams = {
  peer?: InputPeer;
  q: string;
  filter: MessagesFilter;
  offset_id: number;
  add_offset?: number;
  limit: number;
  min_date?: number;
  max_date?: number;
};
type GetSearchCountersParams = {peer: InputPeer; filters: MessagesFilter[]};
type SaveDraftParams = SendMessageParams;
type SaveFilePartParams = {
  file_id: string | number;
  file_part: number;
  file_total_parts?: number;
  bytes: Uint8Array | ArrayBuffer;
};
type SendMediaParams = SendMessageParams & {media: InputMedia};
type GetFileParams = {location: InputFileLocation; offset: string | number; limit: number};
type SendMultiMediaParams = {
  peer: InputPeer;
  multi_media: InputSingleMedia[];
  schedule_date?: number;
};
type UpdateDialogFilterParams = {id: number; filter?: DialogFilter};
type UpdateDialogFiltersOrderParams = {order: number[]};
type ScheduledMessagesParams = {peer: InputPeer; id: number[]};
type GetScheduledHistoryParams = {peer: InputPeer};
type GetFullUserParams = {id: InputUser};
type NotifySettingsParams = {peer: InputNotifyPeer};
type UpdateNotifySettingsParams = NotifySettingsParams & {settings: InputPeerNotifySettings};

type BootstrapResult =
  boolean |
  Config |
  HelpAppConfig |
  MessagesDialogFilters |
  PeerNotifySettings |
  UpdatesState |
  User[];

export type ChatNotesWebKApiResult =
  {handled: false} |
  {handled: true; result:
    MessagesDialogs |
    MessagesMessages |
    MessagesPeerDialogs |
    MessagesAffectedHistory |
    MessagesAffectedMessages |
    MessagesSearchCounter[] |
    MessagesMessageEditData |
    MessagesMessageReactionsList |
    MessagesAvailableReactions |
    MessagesReactions |
    MessagesSavedReactionTags |
    MessagesPeerSettings |
    PhotosPhotos |
    UsersUserFull |
    DialogFilterSuggested[] |
    BootstrapResult |
    Updates |
    UploadFile};

function inputPeerUserId(peer?: InputPeer): string | undefined {
  return peer?._ === 'inputPeerUser' ? String(peer.user_id) : undefined;
}

function inputUserId(user?: InputUser): string | undefined {
  return user?._ === 'inputUser' ? String(user.user_id) : undefined;
}

function isPositiveLimit(value: unknown): value is number {
  return typeof(value) === 'number' && Number.isSafeInteger(value) && value > 0;
}

function isOptionalScheduleDate(value: unknown): value is number | undefined {
  return value === undefined || (typeof(value) === 'number' && Number.isSafeInteger(value) && value > 0);
}

function chatNotesConfig(): Config {
  const now = Date.now() / 1000 | 0;
  return {
    _: 'config',
    pFlags: {},
    date: now,
    expires: now + 86400,
    test_mode: false,
    this_dc: 1,
    dc_options: [],
    dc_txt_domain_name: '',
    chat_size_max: 1,
    megagroup_size_max: 1,
    forwarded_count_max: 100,
    online_update_period_ms: 30000,
    offline_blur_timeout_ms: 5000,
    offline_idle_timeout_ms: 30000,
    online_cloud_timeout_ms: 300000,
    notify_cloud_delay_ms: 3000,
    notify_default_delay_ms: 1500,
    push_chat_period_ms: 60000,
    push_chat_limit: 2,
    edit_time_limit: 2147483647,
    revoke_time_limit: 2147483647,
    revoke_pm_time_limit: 2147483647,
    rating_e_decay: 2419200,
    stickers_recent_limit: 0,
    channels_read_media_period: 0,
    call_receive_timeout_ms: 20000,
    call_ring_timeout_ms: 90000,
    call_connect_timeout_ms: 30000,
    call_packet_timeout_ms: 10000,
    me_url_prefix: 'https://t.me/',
    caption_length_max: 65535,
    message_length_max: 65535,
    webfile_dc_id: 1
  };
}

export class ChatNotesWebKApiBridge {
  private readonly uploadParts = new Map<string, {parts: Map<number, Uint8Array>; totalParts?: number}>();
  private readonly downloadedFiles = new Map<string, Promise<Uint8Array>>();

  constructor(private readonly adapter: ChatNotesCompatibilityAdapter) {}

  private async loadAttachments(notes: ChatNotesNote[]): Promise<Map<string, ChatNotesAttachment>> {
    const ids = [...new Set(notes.flatMap((note) => note.attachmentId ? [note.attachmentId] : []))];
    const attachments = await Promise.all(ids.map((id) => this.adapter.getAttachment(id)));
    return new Map(attachments.map((attachment) => [attachment.id, attachment]));
  }

  private async loadAllNotes(folderId?: string): Promise<ChatNotesNote[]> {
    const notes: ChatNotesNote[] = [];
    let cursor: string | undefined;
    do {
      const page = await this.adapter.listNotes({folderId, cursor, limit: NOTES_PAGE_SIZE});
      notes.push(...page.notes);
      cursor = page.nextCursor;
    } while(cursor);
    return notes;
  }

  private async sessionUser(): Promise<ChatNotesUser> {
    const session = await this.adapter.getSession();
    if(!session) throw new Error('Chat Notes session is required by the WebK API bridge.');
    return session.user;
  }

  private async getDialogs(params: GetDialogsParams): Promise<MessagesDialogs> {
    const [user, folders, notes] = await Promise.all([
      this.sessionUser(),
      this.adapter.listFolders(),
      this.loadAllNotes()
    ]);
    const all = buildChatNotesWebKDialogs({user, folders, notes}, await this.loadAttachments(notes));
    const offsetPeerId = inputPeerUserId(params.offset_peer);
    const offsetIndex = offsetPeerId ? all.dialogs.findIndex((dialog) =>
      dialog._ === 'dialog' && String(dialog.peer._ === 'peerUser' && dialog.peer.user_id) === offsetPeerId) : -1;
    const start = offsetIndex >= 0 ? offsetIndex + 1 : 0;
    const dialogs = all.dialogs.slice(start, start + params.limit);
    const peerIds = new Set(dialogs.flatMap((dialog) =>
      dialog._ === 'dialog' && dialog.peer._ === 'peerUser' ? [String(dialog.peer.user_id)] : []));
    const topMessageIds = new Set(dialogs.flatMap((dialog) =>
      dialog._ === 'dialog' && dialog.top_message ? [dialog.top_message] : []));

    return {
      _: 'messages.dialogsSlice',
      count: all.dialogs.length,
      dialogs,
      messages: all.messages.filter((message) => topMessageIds.has(message.id)),
      chats: [],
      users: all.users.filter((candidate) =>
        candidate._ === 'user' && (candidate.pFlags.self || peerIds.has(String(candidate.id))))
    };
  }

  private async getDialogFilters(): Promise<MessagesDialogFilters> {
    const folders = await this.adapter.listFolders();
    return {
      _: 'messages.dialogFilters',
      pFlags: {},
      filters: folders.map(chatNotesFolderToWebKDialogFilter)
    };
  }

  private async updateDialogFilter(params: UpdateDialogFilterParams): Promise<boolean> {
    const folders = await this.adapter.listFolders();
    const existing = folders.find((folder) => folder.folderNumber === params.id);
    if(!params.filter) {
      if(existing) await this.adapter.deleteFolder(existing.id);
      return true;
    }
    if(params.filter._ !== 'dialogFilter') {
      throw new Error('Chat Notes supports only regular dialog folders.');
    }
    const title = params.filter.title.text.trim();
    if(!title) throw new Error('Chat Notes folder title is required.');
    if(existing) {
      await this.adapter.updateFolder(existing.id, {
        title,
        icon: params.filter.emoticon,
        revision: existing.revision
      });
      return true;
    }
    const created = await this.adapter.createFolder({
      title,
      icon: params.filter.emoticon,
      preferredFolderNumber: params.id
    });
    if(created.folderNumber !== params.id) {
      throw new Error('Chat Notes folder numbering is out of sync with Web K.');
    }
    return true;
  }

  private async updateDialogFiltersOrder(params: UpdateDialogFiltersOrderParams): Promise<boolean> {
    const folders = await this.adapter.listFolders();
    const byNumber = new Map(folders.map((folder) => [folder.folderNumber, folder.id]));
    const orderedIds = params.order.flatMap((number): string[] => {
      const id = byNumber.get(number);
      return id ? [id] : [];
    });
    for(const folder of folders) {
      if(!orderedIds.includes(folder.id)) orderedIds.push(folder.id);
    }
    await this.adapter.reorderFolders(orderedIds);
    return true;
  }

  private resolveFolder(peer: InputPeer, folders: ChatNotesFolder[]): {
    folderId: string | typeof CHAT_NOTES_UNFILED_FOLDER_ID;
    user: User.user;
  } | undefined {
    const peerId = inputPeerUserId(peer);
    if(!peerId) return undefined;
    if(peerId === String(folderIdToWebKPeerId(CHAT_NOTES_UNFILED_FOLDER_ID))) {
      return {folderId: CHAT_NOTES_UNFILED_FOLDER_ID, user: chatNotesUnfiledToWebKUser()};
    }
    const folder = folders.find((candidate) => String(folderIdToWebKPeerId(candidate.id)) === peerId);
    return folder ? {folderId: folder.id, user: chatNotesFolderToWebKUser(folder)} : undefined;
  }

  private async getHistory(params: GetHistoryParams): Promise<MessagesMessages> {
    const [user, folders] = await Promise.all([this.sessionUser(), this.adapter.listFolders()]);
    const resolved = this.resolveFolder(params.peer, folders);
    if(!resolved) return {_: 'messages.messages', messages: [], topics: [], chats: [], users: [
      buildChatNotesWebKHistory(user, CHAT_NOTES_UNFILED_FOLDER_ID, []).users[0]
    ]};

    const allNotes = resolved.folderId === CHAT_NOTES_UNFILED_FOLDER_ID ?
      (await this.loadAllNotes()).filter((note) => !note.folderId) :
      await this.loadAllNotes(resolved.folderId);
    const all = buildChatNotesWebKHistory(
      user,
      resolved.folderId,
      allNotes,
      await this.loadAttachments(allNotes)
    );
    const offsetIndex = params.offset_id ? all.messages.findIndex((message) => message.id === params.offset_id) : -1;
    const start = Math.max(0, (offsetIndex >= 0 ? offsetIndex + 1 : 0) + params.add_offset);
    const messages = all.messages.slice(start, start + params.limit);

    return {
      _: 'messages.messagesSlice',
      pFlags: {},
      count: all.messages.length,
      messages,
      topics: [],
      chats: [],
      users: [...all.users, resolved.user]
    };
  }

  private async getPeerDialogs(params: GetPeerDialogsParams): Promise<MessagesPeerDialogs> {
    const [user, folders, notes] = await Promise.all([
      this.sessionUser(),
      this.adapter.listFolders(),
      this.loadAllNotes()
    ]);
    const all = buildChatNotesWebKDialogs({user, folders, notes}, await this.loadAttachments(notes));
    const requestedPeerIds = new Set(params.peers.flatMap((dialogPeer) => {
      if(dialogPeer._ !== 'inputDialogPeer') return [];
      const peerId = inputPeerUserId(dialogPeer.peer);
      return peerId ? [peerId] : [];
    }));
    const dialogs = all.dialogs.filter((dialog) =>
      dialog._ === 'dialog' &&
      dialog.peer._ === 'peerUser' &&
      requestedPeerIds.has(String(dialog.peer.user_id))
    );
    const topMessageIds = new Set(dialogs.flatMap((dialog) =>
      dialog._ === 'dialog' && dialog.top_message ? [dialog.top_message] : []));

    return {
      _: 'messages.peerDialogs',
      dialogs,
      messages: all.messages.filter((message) => topMessageIds.has(message.id)),
      chats: [],
      users: all.users.filter((candidate) =>
        candidate._ === 'user' &&
        (candidate.pFlags.self || requestedPeerIds.has(String(candidate.id)))
      ),
      state: {
        _: 'updates.state',
        pts: Math.max(1, ...notes.map((note) => note.messageNumber)),
        qts: 0,
        date: Date.now() / 1000 | 0,
        seq: 0,
        unread_count: 0
      }
    };
  }

  private async sendMessage(params: SendMessageParams): Promise<Updates.updateShortSentMessage> {
    const folders = await this.adapter.listFolders();
    const resolved = this.resolveFolder(params.peer, folders);
    if(!resolved) throw new Error('Chat Notes folder was not found for messages.sendMessage.');
    const replyToMessageNumber = params.reply_to?._ === 'inputReplyToMessage' ?
      params.reply_to.reply_to_msg_id : undefined;
    const replyToNote = replyToMessageNumber ? await this.findNoteByMessageNumber(replyToMessageNumber) : undefined;
    if(replyToMessageNumber && !replyToNote) {
      throw new Error('Chat Notes reply target was not found for messages.sendMessage.');
    }
    const formatting = (params.entities ?? [])
    .map(webKEntityToChatNotesFormatting)
    .filter((entity): entity is NonNullable<typeof entity> => !!entity);
    const note = await this.adapter.createNote({
      folderId: resolved.folderId === CHAT_NOTES_UNFILED_FOLDER_ID ? undefined : resolved.folderId,
      content: params.message,
      contentFormat: formatting.length ? 'markdown' : 'plain',
      formatting,
      replyToNoteId: replyToNote?.id,
      remindAt: params.schedule_date ? new Date(params.schedule_date * 1000).toISOString() : undefined
    });
    return {
      _: 'updateShortSentMessage',
      pFlags: {out: true},
      id: note.messageNumber,
      pts: note.messageNumber,
      pts_count: 1,
      date: Math.floor(Date.parse(note.createdAt) / 1000)
    };
  }

  private async findNoteByMessageNumber(messageNumber: number): Promise<ChatNotesNote | undefined> {
    const notes = await this.loadAllNotes();
    return notes.find((note) => note.messageNumber === messageNumber);
  }

  private async editMessage(params: EditMessageParams): Promise<Updates.updates> {
    const [session, note] = await Promise.all([
      this.sessionUser(),
      this.findNoteByMessageNumber(params.id)
    ]);
    if(!note) throw new Error('Chat Notes message was not found for messages.editMessage.');
    const updated = await this.adapter.updateNote(note.id, {
      folderId: note.folderId,
      content: params.message,
      contentFormat: params.entities?.length ? 'markdown' : note.contentFormat,
      attachmentId: note.attachmentId,
      groupId: note.groupId,
      formatting: params.entities ? params.entities
      .map(webKEntityToChatNotesFormatting)
      .filter((entity): entity is NonNullable<typeof entity> => !!entity) : note.formatting,
      replyToNoteId: note.replyToNoteId,
      forwardedFromNoteId: note.forwardedFromNoteId,
      isPinned: note.isPinned,
      reactions: note.reactions,
      remindAt: params.schedule_date ? new Date(params.schedule_date * 1000).toISOString() : note.remindAt,
      revision: note.revision
    });
    return {
      _: 'updates',
      updates: [{
        _: 'updateEditMessage',
        message: chatNotesNoteToWebKMessage(updated, session.telegramUserId),
        pts: updated.messageNumber,
        pts_count: 1
      }],
      users: [],
      chats: [],
      date: Math.floor(Date.parse(updated.updatedAt) / 1000),
      seq: 0
    };
  }

  private async getMessages(params: GetMessagesParams): Promise<MessagesMessages> {
    const [user, notes] = await Promise.all([this.sessionUser(), this.loadAllNotes()]);
    const requested = new Set(params.id.flatMap((input) => input._ === 'inputMessageID' ? [input.id] : []));
    const selected = notes.filter((note) => requested.has(note.messageNumber));
    return buildChatNotesWebKMessages(user, selected, await this.loadAttachments(selected));
  }

  private async forwardMessages(params: ForwardMessagesParams): Promise<Updates.updates> {
    const [user, folders, notes] = await Promise.all([
      this.sessionUser(),
      this.adapter.listFolders(),
      this.loadAllNotes()
    ]);
    const target = this.resolveFolder(params.to_peer, folders);
    if(!target) throw new Error('Chat Notes target folder was not found for messages.forwardMessages.');
    const byMessageNumber = new Map(notes.map((note) => [note.messageNumber, note]));
    const sourceNotes = params.id.map((id) => byMessageNumber.get(id));
    if(sourceNotes.some((note) => !note)) {
      throw new Error('Chat Notes source message was not found for messages.forwardMessages.');
    }
    const created = [] as ChatNotesNote[];
    for(const source of sourceNotes) {
      created.push(await this.adapter.createNote({
        folderId: target.folderId === CHAT_NOTES_UNFILED_FOLDER_ID ? undefined : target.folderId,
        content: source!.content,
        contentFormat: source!.contentFormat,
        formatting: source!.formatting,
        attachmentId: source!.attachmentId,
        groupId: source!.groupId,
        forwardedFromNoteId: source!.id,
        remindAt: params.schedule_date ? new Date(params.schedule_date * 1000).toISOString() : undefined
      }));
    }
    const numbers = new Map([...notes, ...created].map((note) => [note.id, note.messageNumber]));
    return {
      _: 'updates',
      updates: created.map((note) => ({
        _: 'updateNewMessage',
        message: chatNotesNoteToWebKMessage(note, user.telegramUserId, numbers),
        pts: note.messageNumber,
        pts_count: 1
      })),
      users: [],
      chats: [],
      date: Date.now() / 1000 | 0,
      seq: 0
    };
  }

  private async updatePinnedMessage(params: UpdatePinnedMessageParams): Promise<Updates.updates> {
    const [user, note] = await Promise.all([this.sessionUser(), this.findNoteByMessageNumber(params.id)]);
    if(!note) throw new Error('Chat Notes message was not found for messages.updatePinnedMessage.');
    const updated = await this.adapter.updateNote(note.id, {
      folderId: note.folderId,
      content: note.content,
      contentFormat: note.contentFormat,
      attachmentId: note.attachmentId,
      groupId: note.groupId,
      formatting: note.formatting,
      replyToNoteId: note.replyToNoteId,
      forwardedFromNoteId: note.forwardedFromNoteId,
      isPinned: !params.unpin,
      reactions: note.reactions,
      remindAt: note.remindAt,
      revision: note.revision
    });
    return {
      _: 'updates',
      updates: [{
        _: 'updatePinnedMessages',
        pFlags: {pinned: updated.isPinned || undefined},
        peer: updated.folderId ? {_: 'peerUser', user_id: folderIdToWebKPeerId(updated.folderId)} :
          {_: 'peerUser', user_id: folderIdToWebKPeerId(CHAT_NOTES_UNFILED_FOLDER_ID)},
        messages: [updated.messageNumber],
        pts: updated.messageNumber,
        pts_count: 1
      }],
      users: [chatNotesUserToWebKUser(user)],
      chats: [],
      date: Date.now() / 1000 | 0,
      seq: 0
    };
  }

  private async sendReaction(params: SendReactionParams): Promise<Updates.updates> {
    const note = await this.findNoteByMessageNumber(params.msg_id);
    if(!note) throw new Error('Chat Notes message was not found for messages.sendReaction.');
    const reactions = (params.reaction ?? []).flatMap((reaction) =>
      reaction._ === 'reactionEmoji' ? [reaction.emoticon] : []);
    const updated = await this.adapter.updateNote(note.id, {
      folderId: note.folderId,
      content: note.content,
      contentFormat: note.contentFormat,
      attachmentId: note.attachmentId,
      groupId: note.groupId,
      formatting: note.formatting,
      replyToNoteId: note.replyToNoteId,
      forwardedFromNoteId: note.forwardedFromNoteId,
      isPinned: note.isPinned,
      reactions,
      remindAt: note.remindAt,
      revision: note.revision
    });
    const message = chatNotesNoteToWebKMessage(updated, (await this.sessionUser()).telegramUserId);
    return {
      _: 'updates',
      updates: [{
        _: 'updateMessageReactions',
        peer: message.peer_id,
        msg_id: message.id,
        reactions: message.reactions ?? {_: 'messageReactions', pFlags: {}, results: []},
        pts: updated.messageNumber,
        pts_count: 1
      }],
      users: [],
      chats: [],
      date: Date.now() / 1000 | 0,
      seq: 0
    };
  }

  private filterNotes(
    notes: ChatNotesNote[],
    params: SearchParams,
    attachmentsById: ReadonlyMap<string, ChatNotesAttachment> = new Map()
  ): ChatNotesNote[] {
    const query = params.q.trim().toLocaleLowerCase();
    return notes.filter((note) => {
      if(query && !note.content.toLocaleLowerCase().includes(query)) return false;
      if(params.filter._ === 'inputMessagesFilterPinned' && !note.isPinned) return false;
      const attachment = note.attachmentId ? attachmentsById.get(note.attachmentId) : undefined;
      if(params.filter._ === 'inputMessagesFilterUrl' &&
        !note.formatting.some((entity) => entity.type === 'url' || entity.type === 'text_url')) return false;
      if(params.filter._ === 'inputMessagesFilterPhotos' && attachment?.kind !== 'photo') return false;
      if(params.filter._ === 'inputMessagesFilterVideo' && attachment?.kind !== 'video') return false;
      if(params.filter._ === 'inputMessagesFilterPhotoVideo' &&
        attachment?.kind !== 'photo' && attachment?.kind !== 'video') return false;
      if(params.filter._ === 'inputMessagesFilterDocument' && attachment?.kind !== 'document') return false;
      if(params.filter._ === 'inputMessagesFilterGif' && attachment?.kind !== 'animation') return false;
      if(params.filter._ === 'inputMessagesFilterVoice' && attachment?.kind !== 'voice') return false;
      if(params.filter._ === 'inputMessagesFilterMusic' && attachment?.kind !== 'audio') return false;
      if(params.filter._ === 'inputMessagesFilterRoundVideo' && attachment?.kind !== 'video_note') return false;
      if(params.filter._ === 'inputMessagesFilterRoundVoice' &&
        attachment?.kind !== 'voice' && attachment?.kind !== 'video_note') return false;
      if(![
        'inputMessagesFilterEmpty', 'inputMessagesFilterPinned', 'inputMessagesFilterUrl',
        'inputMessagesFilterPhotos', 'inputMessagesFilterVideo', 'inputMessagesFilterPhotoVideo',
        'inputMessagesFilterDocument', 'inputMessagesFilterGif', 'inputMessagesFilterVoice',
        'inputMessagesFilterMusic', 'inputMessagesFilterRoundVideo', 'inputMessagesFilterRoundVoice'
      ].includes(params.filter._)) return false;
      const createdAt = Date.parse(note.createdAt) / 1000;
      return (!params.min_date || createdAt >= params.min_date) && (!params.max_date || createdAt <= params.max_date);
    });
  }

  private async search(params: SearchParams): Promise<MessagesMessages> {
    const [user, folders] = await Promise.all([this.sessionUser(), this.adapter.listFolders()]);
    const resolved = params.peer ? this.resolveFolder(params.peer, folders) : undefined;
    const notes = resolved?.folderId === CHAT_NOTES_UNFILED_FOLDER_ID ?
      (await this.loadAllNotes()).filter((note) => !note.folderId) :
      await this.loadAllNotes(resolved?.folderId);
    const attachments = await this.loadAttachments(notes);
    const filtered = this.filterNotes(notes, params, attachments)
    .sort((left, right) => right.messageNumber - left.messageNumber);
    const offsetIndex = params.offset_id ? filtered.findIndex((note) => note.messageNumber === params.offset_id) : -1;
    const start = Math.max(0, (offsetIndex >= 0 ? offsetIndex + 1 : 0) + (params.add_offset ?? 0));
    const page = filtered.slice(start, start + params.limit);
    const result = resolved ? buildChatNotesWebKHistory(user, resolved.folderId, page, attachments) :
      buildChatNotesWebKMessages(user, page, attachments);
    return {...result, _: 'messages.messagesSlice', pFlags: {}, count: filtered.length};
  }

  private async getAllDrafts(): Promise<Updates.updates> {
    const [drafts, notes] = await Promise.all([this.adapter.listDrafts(), this.loadAllNotes()]);
    const noteNumbersById = new Map(notes.map((note) => [note.id, note.messageNumber]));
    return {
      _: 'updates',
      updates: drafts.map((draft) => ({
        _: 'updateDraftMessage',
        peer: {
          _: 'peerUser',
          user_id: folderIdToWebKPeerId(draft.folderId ?? CHAT_NOTES_UNFILED_FOLDER_ID)
        },
        draft: {
          _: 'draftMessage',
          pFlags: {},
          message: draft.content,
          entities: draft.formatting.map(chatNotesFormattingToWebKEntity),
          reply_to: draft.replyToNoteId && noteNumbersById.has(draft.replyToNoteId) ? {
            _: 'inputReplyToMessage',
            reply_to_msg_id: noteNumbersById.get(draft.replyToNoteId)!
          } : undefined,
          date: Math.floor(Date.parse(draft.updatedAt) / 1000)
        }
      })),
      users: [],
      chats: [],
      date: Date.now() / 1000 | 0,
      seq: 0
    };
  }

  private async saveDraft(params: SaveDraftParams): Promise<boolean> {
    const folders = await this.adapter.listFolders();
    const resolved = this.resolveFolder(params.peer, folders);
    if(!resolved) throw new Error('Chat Notes folder was not found for messages.saveDraft.');
    const replyNumber = params.reply_to?._ === 'inputReplyToMessage' ? params.reply_to.reply_to_msg_id : undefined;
    const reply = replyNumber ? await this.findNoteByMessageNumber(replyNumber) : undefined;
    if(replyNumber && !reply) throw new Error('Chat Notes reply target was not found for messages.saveDraft.');
    await this.adapter.saveDraft({
      folderId: resolved.folderId === CHAT_NOTES_UNFILED_FOLDER_ID ? undefined : resolved.folderId,
      content: params.message,
      formatting: (params.entities ?? []).map(webKEntityToChatNotesFormatting)
      .filter((entity): entity is NonNullable<typeof entity> => !!entity),
      replyToNoteId: reply?.id
    });
    return true;
  }

  private async clearAllDrafts(): Promise<boolean> {
    const drafts = await this.adapter.listDrafts();
    await Promise.all(drafts.map((draft) => this.adapter.saveDraft({
      folderId: draft.folderId,
      content: '',
      formatting: []
    })));
    return true;
  }

  private reactionCatalog(): MessagesReactions.messagesReactions {
    return {
      _: 'messages.reactions',
      hash: 1,
      reactions: DEFAULT_REACTIONS.map((emoticon) => ({_: 'reactionEmoji', emoticon}))
    };
  }

  private async getSavedReactionTags(peer?: InputPeer): Promise<MessagesSavedReactionTags.messagesSavedReactionTags> {
    const folders = await this.adapter.listFolders();
    const resolved = peer ? this.resolveFolder(peer, folders) : undefined;
    const notes = await this.loadAllNotes(resolved?.folderId === CHAT_NOTES_UNFILED_FOLDER_ID ? undefined : resolved?.folderId);
    const scopedNotes = resolved?.folderId === CHAT_NOTES_UNFILED_FOLDER_ID ? notes.filter((note) => !note.folderId) : notes;
    const counts = new Map<string, number>();
    for(const note of scopedNotes) {
      for(const emoticon of note.reactions) counts.set(emoticon, (counts.get(emoticon) ?? 0) + 1);
    }
    return {
      _: 'messages.savedReactionTags',
      hash: 1,
      tags: [...counts].sort((left, right) => right[1] - left[1]).map(([emoticon, count]) => ({
        _: 'savedReactionTag',
        reaction: {_: 'reactionEmoji', emoticon},
        count
      }))
    };
  }

  private saveFilePart(params: SaveFilePartParams): boolean {
    if(!Number.isSafeInteger(params.file_part) || params.file_part < 0) {
      throw new Error('Invalid Chat Notes upload part number.');
    }
    const bytes = params.bytes instanceof Uint8Array ? params.bytes : new Uint8Array(params.bytes);
    const key = String(params.file_id);
    const upload = this.uploadParts.get(key) ?? {parts: new Map<number, Uint8Array>()};
    upload.parts.set(params.file_part, bytes.slice());
    if(params.file_total_parts !== undefined) upload.totalParts = params.file_total_parts;
    const size = [...upload.parts.values()].reduce((total, part) => total + part.byteLength, 0);
    if(size > 20 * 1024 * 1024) {
      this.uploadParts.delete(key);
      throw new Error('Chat Notes attachments are limited to 20 MB by the Telegram Bot API.');
    }
    this.uploadParts.set(key, upload);
    return true;
  }

  private attachmentKind(media: InputMedia.inputMediaUploadedDocument): ChatNotesAttachmentKind {
    const video = media.attributes.find((attribute) => attribute._ === 'documentAttributeVideo');
    if(video?._ === 'documentAttributeVideo') {
      return video.pFlags.round_message ? 'video_note' : 'video';
    }
    const audio = media.attributes.find((attribute) => attribute._ === 'documentAttributeAudio');
    if(audio?._ === 'documentAttributeAudio') return audio.pFlags.voice ? 'voice' : 'audio';
    if(media.attributes.some((attribute) => attribute._ === 'documentAttributeAnimated')) return 'animation';
    return 'document';
  }

  private consumeUpload(file: InputFile, mimeType: string): {blob: Blob; fileName: string} {
    if(file._ !== 'inputFile' && file._ !== 'inputFileBig') throw new Error('Unsupported Chat Notes input file.');
    const key = String(file.id);
    const upload = this.uploadParts.get(key);
    const expected = file.parts;
    if(!upload || upload.parts.size !== expected) throw new Error('Chat Notes upload is incomplete.');
    const parts: Uint8Array[] = [];
    for(let index = 0; index < expected; index++) {
      const part = upload.parts.get(index);
      if(!part) throw new Error('Chat Notes upload is missing a file part.');
      parts.push(part);
    }
    this.uploadParts.delete(key);
    return {
      blob: new Blob(parts.map((part) => Uint8Array.from(part).buffer), {type: mimeType}),
      fileName: file.name
    };
  }

  private async sendMedia(params: SendMediaParams, groupId?: string): Promise<Updates.updates> {
    const folders = await this.adapter.listFolders();
    const resolved = this.resolveFolder(params.peer, folders);
    if(!resolved) throw new Error('Chat Notes folder was not found for messages.sendMedia.');
    let file: InputFile;
    let kind: ChatNotesAttachmentKind;
    let mimeType: string;
    if(params.media._ === 'inputMediaUploadedPhoto') {
      file = params.media.file;
      kind = 'photo';
      mimeType = 'image/jpeg';
    } else if(params.media._ === 'inputMediaUploadedDocument') {
      file = params.media.file;
      kind = this.attachmentKind(params.media);
      mimeType = params.media.mime_type;
    } else {
      throw new Error(`Chat Notes does not support media type: ${params.media._}.`);
    }
    const upload = this.consumeUpload(file, mimeType);
    const attachment = await this.adapter.uploadAttachment(kind, upload.blob, upload.fileName);
    const formatting = (params.entities ?? []).map(webKEntityToChatNotesFormatting)
    .filter((entity): entity is NonNullable<typeof entity> => !!entity);
    const note = await this.adapter.createNote({
      folderId: resolved.folderId === CHAT_NOTES_UNFILED_FOLDER_ID ? undefined : resolved.folderId,
      content: params.message,
      contentFormat: formatting.length ? 'markdown' : 'plain',
      formatting,
      attachmentId: attachment.id,
      groupId,
      remindAt: params.schedule_date ? new Date(params.schedule_date * 1000).toISOString() : undefined
    });
    const user = await this.sessionUser();
    return {
      _: 'updates',
      updates: [{
        _: 'updateNewMessage',
        message: chatNotesNoteToWebKMessage(
          note,
          user.telegramUserId,
          new Map(),
          new Map([[attachment.id, attachment]])
        ),
        pts: note.messageNumber,
        pts_count: 1
      }],
      users: [],
      chats: [],
      date: Date.now() / 1000 | 0,
      seq: 0
    };
  }

  private async getFile(params: GetFileParams): Promise<UploadFile.uploadFile> {
    if(params.location._ !== 'inputDocumentFileLocation' && params.location._ !== 'inputPhotoFileLocation') {
      throw new Error('Unsupported Chat Notes file location.');
    }
    const attachmentId = String(params.location.id);
    let download = this.downloadedFiles.get(attachmentId);
    if(!download) {
      if(this.downloadedFiles.size >= 3) {
        this.downloadedFiles.delete(this.downloadedFiles.keys().next().value!);
      }
      download = this.adapter.downloadAttachment(attachmentId).catch((error) => {
        this.downloadedFiles.delete(attachmentId);
        throw error;
      });
      this.downloadedFiles.set(attachmentId, download);
    }
    const bytes = await download;
    const offset = Number(params.offset);
    if(!Number.isSafeInteger(offset) || offset < 0 || !isPositiveLimit(params.limit)) {
      throw new Error('Invalid upload.getFile range.');
    }
    return {
      _: 'upload.file',
      type: {_: 'storage.fileUnknown'},
      mtime: Date.now() / 1000 | 0,
      bytes: bytes.slice(offset, offset + params.limit)
    };
  }

  private async sendMultiMedia(params: SendMultiMediaParams): Promise<Updates.updates> {
    const groupId = crypto.randomUUID();
    const updates = [] as Updates.updates['updates'];
    for(const media of params.multi_media) {
      const result = await this.sendMedia({
        peer: params.peer,
        media: media.media,
        message: media.message,
        entities: media.entities,
        schedule_date: params.schedule_date
      }, groupId);
      updates.push(...result.updates);
    }
    return {
      _: 'updates',
      updates,
      users: [],
      chats: [],
      date: Date.now() / 1000 | 0,
      seq: 0
    };
  }

  private async getScheduledHistory(params: GetScheduledHistoryParams): Promise<MessagesMessages> {
    const [user, folders, notes] = await Promise.all([
      this.sessionUser(),
      this.adapter.listFolders(),
      this.loadAllNotes()
    ]);
    const resolved = this.resolveFolder(params.peer, folders);
    if(!resolved) throw new Error('Chat Notes folder was not found for messages.getScheduledHistory.');
    const scheduled = notes.filter((note) =>
      !!note.remindAt &&
      (note.folderId ?? CHAT_NOTES_UNFILED_FOLDER_ID) === resolved.folderId
    );
    const result = buildChatNotesWebKMessages(user, scheduled, await this.loadAttachments(scheduled));
    const remindersByNumber = new Map(scheduled.map((note) => [note.messageNumber, note.remindAt!]));
    for(const message of result.messages) {
      if(message._ !== 'message') continue;
      message.date = Math.floor(Date.parse(remindersByNumber.get(message.id)!) / 1000);
    }
    result.messages.sort((left, right) => {
      const leftDate = left._ === 'message' ? left.date : 0;
      const rightDate = right._ === 'message' ? right.date : 0;
      return leftDate - rightDate || left.id - right.id;
    });
    return result;
  }

  private async getScheduledMessages(params: ScheduledMessagesParams): Promise<MessagesMessages> {
    const [user, notes] = await Promise.all([this.sessionUser(), this.loadAllNotes()]);
    const requested = new Set(params.id);
    const scheduled = notes.filter((note) => note.remindAt && requested.has(note.messageNumber));
    const result = buildChatNotesWebKMessages(user, scheduled, await this.loadAttachments(scheduled));
    const remindersByNumber = new Map(scheduled.map((note) => [note.messageNumber, note.remindAt!]));
    for(const message of result.messages) {
      if(message._ === 'message') {
        message.date = Math.floor(Date.parse(remindersByNumber.get(message.id)!) / 1000);
      }
    }
    return result;
  }

  private async sendScheduledMessages(params: ScheduledMessagesParams): Promise<Updates.updates> {
    const notes = await this.loadAllNotes();
    const byMessageNumber = new Map(notes.map((note) => [note.messageNumber, note]));
    const selected = params.id.map((messageNumber) => byMessageNumber.get(messageNumber));
    if(selected.some((note) => !note?.remindAt)) {
      throw new Error('Chat Notes scheduled message was not found.');
    }
    await Promise.all(selected.map((note) => this.adapter.updateNote(note!.id, {
      folderId: note!.folderId,
      content: note!.content,
      contentFormat: note!.contentFormat,
      attachmentId: note!.attachmentId,
      groupId: note!.groupId,
      formatting: note!.formatting,
      replyToNoteId: note!.replyToNoteId,
      forwardedFromNoteId: note!.forwardedFromNoteId,
      isPinned: note!.isPinned,
      reactions: note!.reactions,
      remindAt: undefined,
      revision: note!.revision
    })));
    return this.scheduledDeletionUpdate(params, true);
  }

  private async deleteScheduledMessages(params: ScheduledMessagesParams): Promise<Updates.updates> {
    const notes = await this.loadAllNotes();
    const byMessageNumber = new Map(notes.map((note) => [note.messageNumber, note]));
    const selected = params.id.map((messageNumber) => byMessageNumber.get(messageNumber));
    if(selected.some((note) => !note?.remindAt)) {
      throw new Error('Chat Notes scheduled message was not found.');
    }
    await Promise.all(selected.map((note) => this.adapter.deleteNote(note!.id)));
    return this.scheduledDeletionUpdate(params, false);
  }

  private scheduledDeletionUpdate(params: ScheduledMessagesParams, sent: boolean): Updates.updates {
    return {
      _: 'updates',
      updates: [{
        _: 'updateDeleteScheduledMessages',
        peer: params.peer._ === 'inputPeerUser' ?
          {_: 'peerUser', user_id: params.peer.user_id} :
          {_: 'peerUser', user_id: 0},
        messages: params.id,
        sent_messages: sent ? params.id : undefined
      }],
      users: [],
      chats: [],
      date: Date.now() / 1000 | 0,
      seq: 0
    };
  }

  private async deleteMessages(params: DeleteMessagesParams): Promise<MessagesAffectedMessages> {
    const notes = await this.loadAllNotes();
    const byMessageNumber = new Map(notes.map((note) => [note.messageNumber, note]));
    const toDelete = params.id.map((messageNumber) => byMessageNumber.get(messageNumber));
    if(toDelete.some((note) => !note)) {
      throw new Error('Chat Notes message was not found for messages.deleteMessages.');
    }
    await Promise.all(toDelete.map((note) => this.adapter.deleteNote(note!.id)));
    return {
      _: 'messages.affectedMessages',
      pts: Math.max(0, ...params.id),
      pts_count: params.id.length
    };
  }

  private async deleteHistory(params: DeleteHistoryParams): Promise<MessagesAffectedHistory> {
    const folders = await this.adapter.listFolders();
    const resolved = this.resolveFolder(params.peer, folders);
    if(!resolved) throw new Error('Chat Notes folder was not found for messages.deleteHistory.');
    const notes = resolved.folderId === CHAT_NOTES_UNFILED_FOLDER_ID ?
      (await this.loadAllNotes()).filter((note) => !note.folderId) :
      await this.loadAllNotes(resolved.folderId);
    await Promise.all(notes.map((note) => this.adapter.deleteNote(note.id)));
    return {
      _: 'messages.affectedHistory',
      pts: Math.max(0, ...notes.map((note) => note.messageNumber)),
      pts_count: notes.length,
      offset: 0
    };
  }

  private async getUsers(): Promise<User[]> {
    const [user, folders] = await Promise.all([this.sessionUser(), this.adapter.listFolders()]);
    return [
      chatNotesUserToWebKUser(user),
      ...folders.map(chatNotesFolderToWebKUser),
      chatNotesUnfiledToWebKUser()
    ];
  }

  private async getFullUser(params: GetFullUserParams): Promise<UsersUserFull> {
    const [session, folders, notes] = await Promise.all([
      this.sessionUser(),
      this.adapter.listFolders(),
      this.loadAllNotes()
    ]);
    const requestedId = params.id._ === 'inputUserSelf' ? session.telegramUserId : inputUserId(params.id);
    const folder = folders.find((candidate) => String(folderIdToWebKPeerId(candidate.id)) === requestedId);
    const isUnfiled = requestedId === String(folderIdToWebKPeerId(CHAT_NOTES_UNFILED_FOLDER_ID));
    const apiUser = folder ? chatNotesFolderToWebKUser(folder) :
      isUnfiled ? chatNotesUnfiledToWebKUser() :
      requestedId === session.telegramUserId ? chatNotesUserToWebKUser(session) : undefined;
    if(!apiUser) throw new Error('Chat Notes user was not found for users.getFullUser.');
    const folderId = folder?.id ?? (isUnfiled ? CHAT_NOTES_UNFILED_FOLDER_ID : undefined);
    const hasScheduled = folderId !== undefined && notes.some((note) =>
      !!note.remindAt && (note.folderId ?? CHAT_NOTES_UNFILED_FOLDER_ID) === folderId);
    return {
      _: 'users.userFull',
      full_user: {
        _: 'userFull',
        pFlags: {
          can_pin_message: folderId !== undefined || undefined,
          has_scheduled: hasScheduled || undefined
        },
        id: apiUser.id,
        settings: {_: 'peerSettings', pFlags: {}},
        notify_settings: {_: 'peerNotifySettings'},
        common_chats_count: 0
      },
      chats: [],
      users: [apiUser]
    };
  }

  private async getPeerSettings(peer: InputPeer): Promise<MessagesPeerSettings> {
    const folders = await this.adapter.listFolders();
    if(!this.resolveFolder(peer, folders)) {
      throw new Error('Chat Notes folder was not found for messages.getPeerSettings.');
    }
    return {
      _: 'messages.peerSettings',
      settings: {_: 'peerSettings', pFlags: {}},
      chats: [],
      users: []
    };
  }

  private async notificationScope(peer: InputNotifyPeer): Promise<string> {
    if(peer._ === 'inputNotifyUsers') return 'global:users';
    if(peer._ === 'inputNotifyChats') return 'global:chats';
    if(peer._ === 'inputNotifyBroadcasts') return 'global:broadcasts';
    if(peer._ !== 'inputNotifyPeer') throw new Error(`Unsupported Chat Notes notification scope: ${peer._}.`);
    const folders = await this.adapter.listFolders();
    const resolved = this.resolveFolder(peer.peer, folders);
    if(!resolved || resolved.folderId === CHAT_NOTES_UNFILED_FOLDER_ID) {
      throw new Error('Chat Notes folder was not found for notification settings.');
    }
    return `folder:${resolved.folderId}`;
  }

  private async getNotifySettings(params: NotifySettingsParams): Promise<PeerNotifySettings> {
    const settings = await this.adapter.getNotificationSettings(await this.notificationScope(params.peer));
    return {_: 'peerNotifySettings', show_previews: settings.showPreviews, mute_until: settings.muteUntil};
  }

  private async updateNotifySettings(params: UpdateNotifySettingsParams): Promise<boolean> {
    await this.adapter.saveNotificationSettings({
      scope: await this.notificationScope(params.peer),
      showPreviews: params.settings.show_previews ?? true,
      muteUntil: params.settings.mute_until ?? 0
    });
    return true;
  }

  public async tryInvoke(method: string, params: unknown): Promise<ChatNotesWebKApiResult> {
    if(method === 'help.getConfig') {
      return {handled: true, result: chatNotesConfig()};
    }
    if(method === 'help.getAppConfig') {
      return {handled: true, result: {
        _: 'help.appConfig',
        hash: 1,
        config: {emojies_send_dice: []} as unknown as HelpAppConfig.helpAppConfig['config']
      }};
    }
    if(method === 'updates.getState') {
      return {handled: true, result: {
        _: 'updates.state',
        pts: 1,
        qts: 0,
        date: Date.now() / 1000 | 0,
        seq: 0,
        unread_count: 0
      }};
    }
    if(method === 'messages.getDialogFilters') {
      return {handled: true, result: await this.getDialogFilters()};
    }
    if(method === 'messages.getSuggestedDialogFilters') {
      return {handled: true, result: []};
    }
    if(method === 'messages.updateDialogFilter') {
      const value = params as Partial<UpdateDialogFilterParams>;
      if(!Number.isSafeInteger(value?.id) || value.id! < 2) {
        throw new Error('Invalid messages.updateDialogFilter parameters.');
      }
      return {handled: true, result: await this.updateDialogFilter(value as UpdateDialogFilterParams)};
    }
    if(method === 'messages.updateDialogFiltersOrder') {
      const value = params as Partial<UpdateDialogFiltersOrderParams>;
      if(!Array.isArray(value?.order) || !value.order.every((id) => Number.isSafeInteger(id))) {
        throw new Error('Invalid messages.updateDialogFiltersOrder parameters.');
      }
      return {handled: true, result: await this.updateDialogFiltersOrder(value as UpdateDialogFiltersOrderParams)};
    }
    if(method === 'users.getUsers') {
      return {handled: true, result: await this.getUsers()};
    }
    if(method === 'users.getFullUser') {
      const value = params as Partial<GetFullUserParams>;
      if(!value?.id) throw new Error('Invalid users.getFullUser parameters.');
      return {handled: true, result: await this.getFullUser(value as GetFullUserParams)};
    }
    if(method === 'photos.getUserPhotos') {
      return {handled: true, result: {_: 'photos.photos', photos: [], users: []}};
    }
    if(method === 'messages.getPeerSettings') {
      const value = params as Partial<{peer: InputPeer}>;
      if(!value?.peer) throw new Error('Invalid messages.getPeerSettings parameters.');
      return {handled: true, result: await this.getPeerSettings(value.peer)};
    }
    if(method === 'account.getNotifySettings') {
      const value = params as Partial<NotifySettingsParams>;
      if(!value?.peer) throw new Error('Invalid account.getNotifySettings parameters.');
      return {handled: true, result: await this.getNotifySettings(value as NotifySettingsParams)};
    }
    if(method === 'account.updateNotifySettings') {
      const value = params as Partial<UpdateNotifySettingsParams>;
      if(!value?.peer || !value.settings) throw new Error('Invalid account.updateNotifySettings parameters.');
      return {handled: true, result: await this.updateNotifySettings(value as UpdateNotifySettingsParams)};
    }
    if(method === 'account.updateStatus') {
      return {handled: true, result: true};
    }
    if(method === 'messages.getDialogs') {
      const value = params as Partial<GetDialogsParams>;
      if(!isPositiveLimit(value?.limit)) throw new Error('Invalid messages.getDialogs limit.');
      return {handled: true, result: await this.getDialogs(value as GetDialogsParams)};
    }
    if(method === 'messages.getPeerDialogs') {
      const value = params as Partial<GetPeerDialogsParams>;
      if(!Array.isArray(value?.peers) || !value.peers.length) {
        throw new Error('Invalid messages.getPeerDialogs parameters.');
      }
      return {handled: true, result: await this.getPeerDialogs(value as GetPeerDialogsParams)};
    }
    if(method === 'messages.getHistory') {
      const value = params as Partial<GetHistoryParams>;
      if(
        !value?.peer ||
        !isPositiveLimit(value.limit) ||
        !Number.isSafeInteger(value.offset_id) ||
        !Number.isSafeInteger(value.add_offset)
      ) throw new Error('Invalid messages.getHistory parameters.');
      return {handled: true, result: await this.getHistory(value as GetHistoryParams)};
    }
    if(method === 'messages.getMessages') {
      const value = params as Partial<GetMessagesParams>;
      if(!Array.isArray(value?.id)) throw new Error('Invalid messages.getMessages parameters.');
      return {handled: true, result: await this.getMessages(value as GetMessagesParams)};
    }
    if(method === 'messages.getScheduledHistory') {
      const value = params as Partial<GetScheduledHistoryParams>;
      if(!value?.peer) throw new Error('Invalid messages.getScheduledHistory parameters.');
      return {handled: true, result: await this.getScheduledHistory(value as GetScheduledHistoryParams)};
    }
    if(method === 'messages.getScheduledMessages') {
      const value = params as Partial<ScheduledMessagesParams>;
      if(
        !value?.peer || !Array.isArray(value.id) ||
        !value.id.every((id) => Number.isSafeInteger(id) && id > 0)
      ) throw new Error('Invalid messages.getScheduledMessages parameters.');
      return {handled: true, result: await this.getScheduledMessages(value as ScheduledMessagesParams)};
    }
    if(method === 'messages.sendScheduledMessages' || method === 'messages.deleteScheduledMessages') {
      const value = params as Partial<ScheduledMessagesParams>;
      if(
        !value?.peer || !Array.isArray(value.id) || !value.id.length ||
        !value.id.every((id) => Number.isSafeInteger(id) && id > 0)
      ) throw new Error(`Invalid ${method} parameters.`);
      return {
        handled: true,
        result: method === 'messages.sendScheduledMessages' ?
          await this.sendScheduledMessages(value as ScheduledMessagesParams) :
          await this.deleteScheduledMessages(value as ScheduledMessagesParams)
      };
    }
    if(method === 'messages.search' || method === 'messages.searchGlobal') {
      const value = params as Partial<SearchParams>;
      if(
        typeof(value?.q) !== 'string' ||
        !value.filter ||
        !Number.isSafeInteger(value.offset_id) ||
        !isPositiveLimit(value.limit)
      ) throw new Error(`Invalid ${method} parameters.`);
      return {handled: true, result: await this.search(value as SearchParams)};
    }
    if(method === 'messages.getSearchCounters') {
      const value = params as Partial<GetSearchCountersParams>;
      if(!value?.peer || !Array.isArray(value.filters)) {
        throw new Error('Invalid messages.getSearchCounters parameters.');
      }
      const [folders, notes] = await Promise.all([this.adapter.listFolders(), this.loadAllNotes()]);
      const attachments = await this.loadAttachments(notes);
      const resolved = this.resolveFolder(value.peer, folders);
      const folderNotes = notes.filter((note) => resolved?.folderId === CHAT_NOTES_UNFILED_FOLDER_ID ?
        !note.folderId : note.folderId === resolved?.folderId);
      return {handled: true, result: value.filters.map((filter) => ({
        _: 'messages.searchCounter',
        pFlags: {},
        filter,
        count: this.filterNotes(folderNotes, {
          peer: value.peer,
          q: '',
          filter,
          offset_id: 0,
          limit: NOTES_PAGE_SIZE
        }, attachments).length
      }))};
    }
    if(method === 'messages.getAllDrafts') {
      return {handled: true, result: await this.getAllDrafts()};
    }
    if(method === 'messages.clearAllDrafts') {
      return {handled: true, result: await this.clearAllDrafts()};
    }
    if(method === 'messages.saveDraft') {
      const value = params as Partial<SaveDraftParams>;
      if(!value?.peer || typeof(value.message) !== 'string' || value.message.length > 65535) {
        throw new Error('Invalid messages.saveDraft parameters.');
      }
      return {handled: true, result: await this.saveDraft(value as SaveDraftParams)};
    }
    if(method === 'upload.saveFilePart' || method === 'upload.saveBigFilePart') {
      const value = params as Partial<SaveFilePartParams>;
      if(
        value.file_id === undefined ||
        !Number.isSafeInteger(value.file_part) ||
        !(value.bytes instanceof Uint8Array || value.bytes instanceof ArrayBuffer)
      ) throw new Error(`Invalid ${method} parameters.`);
      return {handled: true, result: this.saveFilePart(value as SaveFilePartParams)};
    }
    if(method === 'upload.getFile') {
      const value = params as Partial<GetFileParams>;
      if(!value.location || !isPositiveLimit(value.limit)) {
        throw new Error('Invalid upload.getFile parameters.');
      }
      return {handled: true, result: await this.getFile(value as GetFileParams)};
    }
    if(method === 'messages.sendMessage') {
      const value = params as Partial<SendMessageParams>;
      if(
        !value?.peer ||
        typeof(value.message) !== 'string' ||
        value.message.trim().length === 0 ||
        value.message.length > 65535 ||
        !isOptionalScheduleDate(value.schedule_date)
      ) throw new Error('Invalid messages.sendMessage parameters.');
      return {handled: true, result: await this.sendMessage(value as SendMessageParams)};
    }
    if(method === 'messages.sendMedia') {
      const value = params as Partial<SendMediaParams>;
      if(
        !value?.peer || !value.media || typeof(value.message) !== 'string' ||
        !isOptionalScheduleDate(value.schedule_date)
      ) {
        throw new Error('Invalid messages.sendMedia parameters.');
      }
      return {handled: true, result: await this.sendMedia(value as SendMediaParams)};
    }
    if(method === 'messages.sendMultiMedia') {
      const value = params as Partial<SendMultiMediaParams>;
      if(
        !value?.peer || !Array.isArray(value.multi_media) || !value.multi_media.length ||
        !isOptionalScheduleDate(value.schedule_date)
      ) {
        throw new Error('Invalid messages.sendMultiMedia parameters.');
      }
      return {handled: true, result: await this.sendMultiMedia(value as SendMultiMediaParams)};
    }
    if(method === 'messages.editMessage') {
      const value = params as Partial<EditMessageParams>;
      if(
        !value?.peer ||
        !Number.isSafeInteger(value.id) ||
        typeof(value.message) !== 'string' ||
        value.message.trim().length === 0 ||
        value.message.length > 65535 ||
        !isOptionalScheduleDate(value.schedule_date)
      ) throw new Error('Invalid messages.editMessage parameters.');
      return {handled: true, result: await this.editMessage(value as EditMessageParams)};
    }
    if(method === 'messages.forwardMessages') {
      const value = params as Partial<ForwardMessagesParams>;
      if(
        !value?.from_peer || !value.to_peer || !Array.isArray(value.id) || !value.id.length ||
        !value.id.every((id) => Number.isSafeInteger(id) && id > 0) ||
        !isOptionalScheduleDate(value.schedule_date)
      ) throw new Error('Invalid messages.forwardMessages parameters.');
      return {handled: true, result: await this.forwardMessages(value as ForwardMessagesParams)};
    }
    if(method === 'messages.updatePinnedMessage') {
      const value = params as Partial<UpdatePinnedMessageParams>;
      if(!value?.peer || !Number.isSafeInteger(value.id) || !value.id) {
        throw new Error('Invalid messages.updatePinnedMessage parameters.');
      }
      return {handled: true, result: await this.updatePinnedMessage(value as UpdatePinnedMessageParams)};
    }
    if(method === 'messages.unpinAllMessages') {
      const value = params as Partial<{peer: InputPeer}>;
      if(!value?.peer) throw new Error('Invalid messages.unpinAllMessages parameters.');
      const folders = await this.adapter.listFolders();
      const resolved = this.resolveFolder(value.peer, folders);
      if(!resolved) throw new Error('Chat Notes folder was not found for messages.unpinAllMessages.');
      const notes = (await this.loadAllNotes()).filter((note) =>
        (note.folderId ?? CHAT_NOTES_UNFILED_FOLDER_ID) === resolved.folderId && note.isPinned);
      await Promise.all(notes.map((note) => this.adapter.updateNote(note.id, {
        folderId: note.folderId,
        content: note.content,
        contentFormat: note.contentFormat,
        attachmentId: note.attachmentId,
        groupId: note.groupId,
        formatting: note.formatting,
        replyToNoteId: note.replyToNoteId,
        forwardedFromNoteId: note.forwardedFromNoteId,
        isPinned: false,
        reactions: note.reactions,
        remindAt: note.remindAt,
        revision: note.revision
      })));
      return {handled: true, result: {
        _: 'messages.affectedHistory',
        pts: Math.max(0, ...notes.map((note) => note.messageNumber)),
        pts_count: notes.length,
        offset: 0
      }};
    }
    if(method === 'messages.sendReaction') {
      const value = params as Partial<SendReactionParams>;
      if(!value?.peer || !Number.isSafeInteger(value.msg_id) ||
        (value.reaction !== undefined && !Array.isArray(value.reaction))) {
        throw new Error('Invalid messages.sendReaction parameters.');
      }
      return {handled: true, result: await this.sendReaction(value as SendReactionParams)};
    }
    if(method === 'messages.getAvailableReactions') {
      return {handled: true, result: {
        _: 'messages.availableReactions',
        hash: 1,
        reactions: []
      }};
    }
    if(
      method === 'messages.getTopReactions' ||
      method === 'messages.getRecentReactions' ||
      method === 'messages.getDefaultTagReactions'
    ) {
      return {handled: true, result: this.reactionCatalog()};
    }
    if(method === 'messages.getSavedReactionTags') {
      const value = params as Partial<{peer: InputPeer}>;
      return {handled: true, result: await this.getSavedReactionTags(value.peer)};
    }
    if(method === 'messages.getMessagesReactions') {
      const value = params as Partial<{id: number[]; peer: InputPeer}>;
      if(!value?.peer || !Array.isArray(value.id)) {
        throw new Error('Invalid messages.getMessagesReactions parameters.');
      }
      const [user, notes] = await Promise.all([this.sessionUser(), this.loadAllNotes()]);
      const requested = new Set(value.id);
      const updates = notes.filter((note) => requested.has(note.messageNumber)).map((note) => {
        const message = chatNotesNoteToWebKMessage(note, user.telegramUserId);
        return {
          _: 'updateMessageReactions' as const,
          peer: message.peer_id,
          msg_id: message.id,
          reactions: message.reactions ?? {_: 'messageReactions' as const, pFlags: {}, results: []}
        };
      });
      return {handled: true, result: {
        _: 'updates', updates, users: [], chats: [], date: Date.now() / 1000 | 0, seq: 0
      }};
    }
    if(method === 'messages.getMessageReactionsList') {
      const value = params as Partial<{id: number; reaction?: Reaction; limit: number}>;
      if(!Number.isSafeInteger(value?.id) || !isPositiveLimit(value.limit)) {
        throw new Error('Invalid messages.getMessageReactionsList parameters.');
      }
      const [user, note] = await Promise.all([this.sessionUser(), this.findNoteByMessageNumber(value.id!)]);
      if(!note) throw new Error('Chat Notes message was not found for reactions list.');
      const selected = value.reaction?._ === 'reactionEmoji' ? value.reaction.emoticon : undefined;
      const reactions = note.reactions.filter((reaction) => !selected || reaction === selected)
      .slice(0, value.limit).map((emoticon) => ({
        _: 'messagePeerReaction' as const,
        pFlags: {my: true as const},
        peer_id: {_: 'peerUser' as const, user_id: user.telegramUserId},
        date: Math.floor(Date.parse(note.updatedAt) / 1000),
        reaction: {_: 'reactionEmoji' as const, emoticon}
      }));
      return {handled: true, result: {
        _: 'messages.messageReactionsList',
        count: reactions.length,
        reactions,
        chats: [],
        users: [chatNotesUserToWebKUser(user)]
      }};
    }
    if(method === 'messages.getMessageEditData') {
      const value = params as Partial<{id: number}>;
      if(!Number.isSafeInteger(value?.id)) throw new Error('Invalid messages.getMessageEditData parameters.');
      const note = await this.findNoteByMessageNumber(value.id!);
      if(!note) throw new Error('Chat Notes message was not found for edit data.');
      return {handled: true, result: {
        _: 'messages.messageEditData',
        pFlags: {caption: note.attachmentId ? true : undefined}
      }};
    }
    if(method === 'messages.setTyping') {
      return {handled: true, result: true};
    }
    if(method === 'messages.deleteMessages') {
      const value = params as Partial<DeleteMessagesParams>;
      if(
        !Array.isArray(value?.id) ||
        !value.id.length ||
        !value.id.every((id) => Number.isSafeInteger(id) && id > 0)
      ) throw new Error('Invalid messages.deleteMessages parameters.');
      return {handled: true, result: await this.deleteMessages(value as DeleteMessagesParams)};
    }
    if(method === 'messages.deleteHistory') {
      const value = params as Partial<DeleteHistoryParams>;
      if(!value?.peer) throw new Error('Invalid messages.deleteHistory parameters.');
      return {handled: true, result: await this.deleteHistory(value as DeleteHistoryParams)};
    }
    if(method === 'messages.readHistory') {
      return {handled: true, result: {
        _: 'messages.affectedMessages',
        pts: 0,
        pts_count: 0
      }};
    }
    if(isChatNotesWebKSupportedMethod(method)) {
      throw new Error(`Chat Notes WebK method is registered but has no bridge handler: ${method}.`);
    }
    return {handled: false};
  }
}
